import React from 'react';
import {Modal, Stack} from '@mui/material';
import useSourcePackageBuilderStore from '../../stores/useSourcePackageBuilderStore';
import {useReportStore} from '../../features/products/useReportStore';
import SearchDocumentsPanel from './SearchDocumentsPanel';
import SourcePackageSidePanel from './SourcePackageSidePanel';

// Portal destination — the full layout area (header + content) that Shell shrinks when
// CommunicationHub opens. Portaling here constrains the modal to the content area so
// the hub panel is never overlaid.
function getReportLayoutContainer() {
    return document.getElementById('report-layout-area') || document.body;
}

/**
 * SourcePackageBuilder - Full-screen modal overlay for building source packages.
 * Uses MUI Modal which provides:
 *   - Focus trap (keyboard navigation stays within the modal)
 *   - Backdrop with click-to-close
 *   - Escape key handling (closes the modal)
 *   - Portal rendering (avoids z-index stacking issues)
 *   - ARIA dialog semantics (role="dialog", aria-modal="true")
 *
 * State is managed via Zustand store to prevent re-renders from URL changes.
 * URL params are only read once on mount for initial state.
 */
const SourcePackageBuilder = () => {
    const isOpen = useSourcePackageBuilderStore(s => s.isOpen);
    const close = useSourcePackageBuilderStore(s => s.close);
    // Only show the source-package side panel when there is an active report in the store.
    // When opened from the Spotlight (no report context), we show the document viewer only.
    const hasReportContext = useReportStore(s => Boolean(s.id || s.reportId));

    return (
        <Modal
            open={isOpen}
            onClose={close}
            aria-labelledby="source-package-builder-title"
            container={getReportLayoutContainer}
            sx={{position: 'absolute'}}
            slotProps={{
                backdrop: {
                    sx: {
                        position: 'absolute',
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                    },
                },
            }}
        >
            <Stack
                sx={{
                    p: 2,
                    position: 'absolute',
                    inset: 0,
                    overflow: 'hidden',
                    outline: 'none',
                }}
            >
                <Stack
                    direction="row"
                    sx={(theme) => ({
                        height: '100%',
                        gap: 4,
                        boxShadow: theme.shadows[8],
                    })}
                >
                    <SearchDocumentsPanel/>
                    {hasReportContext && <SourcePackageSidePanel onClose={close}/>}
                </Stack>
            </Stack>
        </Modal>
    );
};

export default SourcePackageBuilder;
